import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftPagination from "components/SoftPagination";

import Table from "examples/Tables/Table";

import RoleChangeRequestsModal from "./Users/components/RoleChangeRequestsModal";
import ViewUserModal from "./Users/components/ViewUserModal";

import { useAuth } from "context/AuthContext";
import {
  getUsers,
  saveUser,
  USERS_API_PAGE_SIZE,
} from "./Users/services/graphqlService";
import { subscribeToGlobal } from "./Users/services/subscriptionService";
import { mockRoleChangeRequests } from "./Users/data/mockUsers";
import {
  sendRoleChangeApprovalEmail,
  sendPilotAppDownloadEmail,
  sendRoleChangeRejectionEmail,
} from "./Users/services/userService";

function getUserDisplayLocation(u) {
  if (!u) return "";
  if (Array.isArray(u.location) && u.location.length > 0) {
    return u.location
      .map((loc) => loc.name || loc.city || "")
      .filter(Boolean)
      .join(", ");
  }
  if (u.location && typeof u.location === "object") {
    const city = u.location.city || "";
    const state = u.location.state || "";
    return city && state ? `${city}, ${state}` : city || state || "";
  }
  const city = u.city || "";
  const state = u.state || "";
  return city && state ? `${city}, ${state}` : city || state || "";
}

function escapeCsvField(value) {
  const s = value == null ? "" : String(value);
  if (/[",\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function downloadCsvFile(filename, csvContent) {
  const blob = new Blob([`\uFEFF${csvContent}`], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function getUserSearchHaystack(u) {
  if (!u) return "";
  const parts = [
    u.name,
    u.profile?.name,
    u.email,
    u.profile?.email,
    u.profileId,
    u.id,
    u.role,
    u.profile?.role,
    u.status,
    u.profile?.status,
    u.phone,
    u.profile?.phone,
    u.mobile,
    u.profile?.mobile,
    u.employeeId,
    u.profile?.employeeId,
    getUserDisplayLocation(u),
  ];
  return parts
    .filter((value) => value != null && String(value).trim())
    .join(" ")
    .toLowerCase();
}

function getUserStatus(u) {
  return String(u?.status || u?.profile?.status || "").trim();
}

function isUserInactive(u) {
  return getUserStatus(u).toLowerCase() === "inactive";
}

function buildUserSavePayload(u, statusOverride) {
  const location = u?.location || u?.profile?.location || {};
  const rawAssociations = u?.association ?? u?.profile?.association;
  let associations;
  if (Array.isArray(rawAssociations) && rawAssociations.length > 0) {
    associations = rawAssociations
      .filter((a) => a && a.entityType && a.role)
      .map((a) => ({
        role: a.role,
        entityType: a.entityType,
        entityId: a.entityId || undefined,
      }));
  }

  return {
    name: u?.name || u?.profile?.name || "",
    email: u?.email || u?.profile?.email || "",
    phone: u?.phone || u?.profile?.phone || "",
    mobile: u?.mobile || u?.profile?.mobile || "",
    locale: u?.locale || u?.profile?.locale || "en-US",
    employeeId: u?.employeeId || u?.profile?.employeeId || "",
    status: statusOverride,
    associations,
    location: {
      city: location.city || u?.city || u?.profile?.city || "",
      state: location.state || u?.state || u?.profile?.state || "",
      locationId:
        location.locationId || u?.locationId || u?.profile?.locationId || "",
    },
  };
}

function UsersPage() {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const currentProfileId = String(profile?.profileId || "").trim();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [roleRequestsModalOpen, setRoleRequestsModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewProfileId, setViewProfileId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  /** Token used to fetch the current page (null = first page). */
  const [pageToken, setPageToken] = useState(null);
  /** nextToken from current response — pass this on Next. */
  const [nextPageToken, setNextPageToken] = useState(null);
  /** Stack of page tokens for Previous. */
  const [tokenHistory, setTokenHistory] = useState([]);
  const [pageLoading, setPageLoading] = useState(false);
  const [deactivatingId, setDeactivatingId] = useState(null);
  const [pendingDeleteUser, setPendingDeleteUser] = useState(null);

  // Role change requests
  const [roleChangeRequests, setRoleChangeRequests] = useState(
    mockRoleChangeRequests,
  );

  const applyUsersResult = (result) => {
    const usersData = result?.data;
    if (!usersData) {
      setUsers([]);
    } else if (Array.isArray(usersData)) {
      setUsers(usersData);
    } else if (usersData.ProfileList && Array.isArray(usersData.ProfileList)) {
      setUsers(usersData.ProfileList);
    } else {
      console.warn("Unexpected users data format:", usersData);
      setUsers([]);
    }
    setNextPageToken(result?.nextToken ?? null);
  };

  const fetchUsersPage = useCallback(
    async (token = null, { isInitial = false } = {}) => {
      if (!user?.id_token) {
        setError("Authentication required");
        setLoading(false);
        setUsers([]);
        setNextPageToken(null);
        return;
      }

      if (isInitial) {
        setLoading(true);
      } else {
        setPageLoading(true);
      }
      setError(null);

      try {
        const result = await getUsers(
          user.id_token,
          USERS_API_PAGE_SIZE,
          token,
        );
        applyUsersResult(result);
        setPageToken(token);
      } catch (err) {
        console.error("Error fetching users:", err);
        setError(err.message || "Failed to load users");
        setUsers([]);
        setNextPageToken(null);
      } finally {
        setLoading(false);
        setPageLoading(false);
      }
    },
    [user?.id_token],
  );

  const fetchData = useCallback(async () => {
    setTokenHistory([]);
    setPageToken(null);
    setNextPageToken(null);
    await fetchUsersPage(null, { isInitial: true });
  }, [fetchUsersPage]);

  const handleNextPage = async () => {
    if (!nextPageToken || pageLoading) return;
    setTokenHistory((prev) => [...prev, pageToken]);
    await fetchUsersPage(nextPageToken);
  };

  const handlePreviousPage = async () => {
    if (tokenHistory.length === 0 || pageLoading) return;
    const prevHistory = [...tokenHistory];
    const previousToken = prevHistory.pop();
    setTokenHistory(prevHistory);
    await fetchUsersPage(previousToken ?? null);
  };

  useEffect(() => {
    if (user?.id_token) {
      fetchData();

      // Set up subscription for real-time updates
      let unsubscribe = null;
      const eventId = "users-update";

      subscribeToGlobal(
        eventId,
        (message) => {
          // When a global message is received, refresh the data
          if (
            message.Type === "user-update" ||
            message.Type === "user" ||
            message.MessageType === "success"
          ) {
            fetchData();
          }
        },
        (err) => {
          console.error("Subscription error:", err);
        },
      )
        .then((unsubFn) => {
          unsubscribe = unsubFn;
        })
        .catch((err) => {
          console.error("Failed to set up subscription:", err);
        });

      return () => {
        if (unsubscribe) {
          unsubscribe();
        }
      };
    }
  }, [user, fetchData]);

  const handleViewUser = (profileId) => {
    setViewProfileId(profileId);
    setViewModalOpen(true);
  };

  const handleEditUser = (userId) => {
    const user = users.find((u) => (u.profileId || u.id) === userId);
    if (user) {
      const profileId = user.profileId || user.id;
      navigate(`/dashboard/add-user?profileId=${profileId}`, {
        state: { userData: user },
      });
    }
  };

  const handleRequestDeleteUser = (userId) => {
    const target = users.find((u) => (u.profileId || u.id) === userId);
    if (!target) return;
    if (isUserInactive(target)) return;

    const profileId = String(target.profileId || target.id || "").trim();
    if (currentProfileId && profileId === currentProfileId) {
      const message = "You cannot delete your own account.";
      setError(message);
      window.dispatchEvent(
        new CustomEvent("globalNotification", {
          detail: {
            type: "error",
            title: "Error",
            message,
          },
        }),
      );
      return;
    }

    setPendingDeleteUser(target);
  };

  const handleConfirmDeleteUser = async () => {
    const target = pendingDeleteUser;
    if (!target) return;

    const profileId = String(target.profileId || target.id || "").trim();
    if (currentProfileId && profileId === currentProfileId) {
      setPendingDeleteUser(null);
      return;
    }

    if (!user?.id_token) {
      setError("Authentication required");
      setPendingDeleteUser(null);
      return;
    }

    setDeactivatingId(profileId);
    setError(null);
    setPendingDeleteUser(null);

    window.dispatchEvent(
      new CustomEvent("globalNotification", {
        detail: {
          type: "info",
          title: "Processing",
          message: "Deleting user... Please wait for confirmation.",
          removeOnSuccess: true,
        },
      }),
    );

    try {
      const payload = buildUserSavePayload(target, "Inactive");
      if (!payload.name || !payload.email) {
        throw new Error("User is missing required name or email");
      }

      const result = await saveUser(payload, user.id_token, profileId);
      if (!result.success) {
        throw new Error(result.message || "Failed to delete user");
      }

      setUsers((prev) =>
        prev.map((u) =>
          (u.profileId || u.id) === profileId
            ? { ...u, status: "Inactive" }
            : u,
        ),
      );
    } catch (err) {
      console.error("Error deactivating user:", err);
      const message = err.message || "Failed to delete user";
      setError(message);
      window.dispatchEvent(
        new CustomEvent("globalNotification", {
          detail: {
            type: "error",
            title: "Error",
            message,
          },
        }),
      );
    } finally {
      setDeactivatingId(null);
    }
  };

  const handleApproveRoleChange = async (requestId) => {
    const request = roleChangeRequests.find((r) => r.id === requestId);
    if (request) {
      const user = users.find((u) => u.id === request.userId);

      // Update user role
      setUsers(
        users.map((u) =>
          u.id === request.userId ? { ...u, role: request.requestedRole } : u,
        ),
      );

      // Mark request as approved
      setRoleChangeRequests(
        roleChangeRequests.map((r) =>
          r.id === requestId ? { ...r, status: "approved" } : r,
        ),
      );

      // Send approval email
      if (user) {
        await sendRoleChangeApprovalEmail(user, request.requestedRole);

        // If changing to pilot, send app download notification
        if (request.requestedRole === "Pilot") {
          await sendPilotAppDownloadEmail(user);
        }
      }
    }
  };

  const handleRejectRoleChange = async (requestId) => {
    const request = roleChangeRequests.find((r) => r.id === requestId);
    if (request) {
      const user = users.find((u) => u.id === request.userId);

      // Mark request as rejected
      setRoleChangeRequests(
        roleChangeRequests.map((r) =>
          r.id === requestId ? { ...r, status: "rejected" } : r,
        ),
      );

      // Send rejection email
      if (user) {
        await sendRoleChangeRejectionEmail(user, request.requestedRole);
      }
    }
  };

  const filteredUsers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => getUserSearchHaystack(u).includes(q));
  }, [users, searchQuery]);

  const canGoPrevious = tokenHistory.length > 0;
  const canGoNext = Boolean(nextPageToken);

  const handleExportCsv = () => {
    if (!filteredUsers.length) return;
    const headers = [
      "Profile ID",
      "Name",
      "Email",
      "Location",
      "Status",
      "Role",
    ];
    const lines = [
      headers.map(escapeCsvField).join(","),
      ...filteredUsers.map((u) => {
        const profileId = u.profileId ?? u.id ?? "";
        const name = u.name || u.profile?.name || "";
        const email = u.email || u.profile?.email || "";
        const location = getUserDisplayLocation(u);
        const status = u.status || u.profile?.status || "";
        const role = u.role || u.profile?.role || "";
        return [profileId, name, email, location, status, role]
          .map(escapeCsvField)
          .join(",");
      }),
    ];
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsvFile(`users-export-${stamp}.csv`, lines.join("\r\n"));
  };

  const columns = [
    { name: "name", align: "left" },
    { name: "email", align: "left" },
    { name: "location", align: "left" },
    { name: "actions", align: "left" },
  ];

  const rows = filteredUsers.map((rowUser) => {
    const rowId = rowUser.profileId || rowUser.id;
    const inactive = isUserInactive(rowUser);
    const isSelf =
      Boolean(currentProfileId) &&
      String(rowId || "").trim() === currentProfileId;
    const isDeactivating = deactivatingId === rowId;
    const canDelete = !inactive && !isSelf;

    return {
    name: (
      <SoftBox display="flex" alignItems="center">
        <SoftTypography variant="button" fontWeight="medium">
          {rowUser.name || rowUser.profile?.name || "N/A"}
        </SoftTypography>
      </SoftBox>
    ),
    email: (
      <SoftTypography variant="caption" color="secondary">
        {rowUser.email || rowUser.profile?.email || "N/A"}
      </SoftTypography>
    ),
    location: (
      <SoftTypography variant="caption" color="secondary">
        {getUserDisplayLocation(rowUser) || "N/A"}
      </SoftTypography>
    ),
    actions: (
      <SoftBox
        display="flex"
        gap={1}
        justifyContent="flex-start"
        alignItems="center"
        width="100%"
      >
        <SoftButton
          variant="text"
          color="info"
          size="small"
          onClick={() => handleViewUser(rowId)}
        >
          <Icon>visibility</Icon>&nbsp;View
        </SoftButton>
        <SoftButton
          variant="text"
          color="dark"
          size="small"
          onClick={() => handleEditUser(rowId)}
        >
          <Icon>edit</Icon>&nbsp;Edit
        </SoftButton>
        {canDelete ? (
          <SoftButton
            variant="text"
            color="error"
            size="small"
            disabled={isDeactivating}
            onClick={() => handleRequestDeleteUser(rowId)}
          >
            <Icon>delete</Icon>&nbsp;
            {isDeactivating ? "Deleting..." : "Delete"}
          </SoftButton>
        ) : (
          <SoftBox
            aria-hidden
            sx={{
              visibility: "hidden",
              pointerEvents: "none",
              display: "inline-flex",
              alignItems: "center",
            }}
          >
            <SoftButton variant="text" color="error" size="small" tabIndex={-1}>
              <Icon>delete</Icon>&nbsp;Delete
            </SoftButton>
          </SoftBox>
        )}
      </SoftBox>
    ),
  };
  });

  const pendingRequestsCount = roleChangeRequests.filter(
    (r) => r.status === "pending",
  ).length;

  return (
    <SoftBox py={3}>
      <SoftBox mb={3}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <SoftTypography variant="h4" fontWeight="bold">
              User Management
            </SoftTypography>
            <SoftTypography variant="body2" color="text">
              Manage users across all portals and entities
            </SoftTypography>
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            display="flex"
            justifyContent="flex-end"
            flexWrap="wrap"
            gap={2}
          >
            <SoftButton
              variant="outlined"
              color="dark"
              onClick={handleExportCsv}
              disabled={
                loading || users.length === 0 || filteredUsers.length === 0
              }
            >
              <Icon sx={{ mr: 1 }}>download</Icon>
              Export to CSV
            </SoftButton>
            <SoftButton
              variant="gradient"
              color="info"
              onClick={() => navigate("/dashboard/add-user")}
            >
              <Icon sx={{ mr: 1 }}>add</Icon>
              Add User
            </SoftButton>
          </Grid>
        </Grid>
      </SoftBox>

      <Card>
        <SoftBox p={2}>
          {error && (
            <SoftBox mb={2}>
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            </SoftBox>
          )}

          {!loading && (users.length > 0 || searchQuery.trim()) && (
            <SoftBox
              mb={2}
              display="flex"
              justifyContent="flex-end"
              sx={{
                maxWidth: "100%",
                "& > div": { width: 360, maxWidth: "100%" },
                "&:focus-within > div": {
                  border: "2px solid",
                  borderColor: "info.main",
                },
                "&:focus-within .MuiInputBase-root.Mui-focused": {
                  boxShadow: "none",
                  borderColor: "transparent !important",
                },
              }}
            >
              <SoftInput
                placeholder="Search by name, email, location, role, status..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                icon={{ component: "search", direction: "left" }}
              />
            </SoftBox>
          )}

          {loading ? (
            <SoftBox
              display="flex"
              justifyContent="center"
              alignItems="center"
              py={8}
            >
              <CircularProgress />
            </SoftBox>
          ) : users.length > 0 ? (
            filteredUsers.length > 0 ? (
              <>
                <Table columns={columns} rows={rows} />
                <SoftBox
                  mt={2}
                  display="flex"
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  flexDirection={{ xs: "column", sm: "row" }}
                  gap={2}
                >
                  <SoftTypography
                    variant="button"
                    color="secondary"
                    fontWeight="regular"
                  >
                    Showing {filteredUsers.length}{" "}
                    {filteredUsers.length === 1 ? "user" : "users"}
                    {searchQuery.trim() ? " (filtered on this page)" : ""}
                    {canGoNext || canGoPrevious ? " · more available" : ""}
                  </SoftTypography>
                  {canGoPrevious || canGoNext ? (
                    <SoftPagination variant="gradient" color="info">
                      <SoftPagination
                        item
                        onClick={handlePreviousPage}
                        disabled={!canGoPrevious || pageLoading}
                      >
                        <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
                      </SoftPagination>
                      <SoftTypography
                        variant="button"
                        fontWeight="medium"
                        mx={1}
                        sx={{ alignSelf: "center" }}
                      >
                        {pageLoading ? "Loading…" : "Prev / Next"}
                      </SoftTypography>
                      <SoftPagination
                        item
                        onClick={handleNextPage}
                        disabled={!canGoNext || pageLoading}
                      >
                        <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
                      </SoftPagination>
                    </SoftPagination>
                  ) : null}
                </SoftBox>
              </>
            ) : (
              <SoftBox textAlign="center" py={3}>
                <SoftTypography variant="body2" color="text">
                  No users match &quot;{searchQuery.trim()}&quot;. Try a
                  different search.
                </SoftTypography>
              </SoftBox>
            )
          ) : (
            <SoftBox textAlign="center" py={3}>
              <SoftTypography variant="h6" color="text">
                No users found. Click "Add User" to create one.
              </SoftTypography>
            </SoftBox>
          )}
        </SoftBox>
      </Card>

      <RoleChangeRequestsModal
        open={roleRequestsModalOpen}
        onClose={() => setRoleRequestsModalOpen(false)}
        requests={roleChangeRequests}
        onApprove={handleApproveRoleChange}
        onReject={handleRejectRoleChange}
      />

      <ViewUserModal
        open={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false);
          setViewProfileId(null);
        }}
        profileId={viewProfileId}
        idToken={user?.id_token}
      />

      <Dialog
        open={!!pendingDeleteUser}
        onClose={() => {
          if (!deactivatingId) setPendingDeleteUser(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete user?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete{" "}
            {pendingDeleteUser?.name ||
              pendingDeleteUser?.profile?.name ||
              "this user"}
            ? They will be deactivated and no longer able to use the platform.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <SoftButton
            variant="text"
            color="secondary"
            disabled={Boolean(deactivatingId)}
            onClick={() => setPendingDeleteUser(null)}
          >
            Cancel
          </SoftButton>
          <SoftButton
            variant="gradient"
            color="error"
            disabled={Boolean(deactivatingId)}
            onClick={handleConfirmDeleteUser}
          >
            {deactivatingId ? "Deleting..." : "Delete"}
          </SoftButton>
        </DialogActions>
      </Dialog>
    </SoftBox>
  );
}

export default UsersPage;
